import { Grid, Stack, Switch, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import { logError } from "@entrilia/shared/logging";
import { convertISODateTime } from "@entrilia/shared/utilities/dateUtils";
import adminApi from "../../../api/adminApi";
import useClientContext from "../../../context/useClientContext";
import useUserContext from "../../../context/useUserContext";
import EntriliaMcpIcon from "../../common/domain-icons/EntriliaMcpIcon";
import KeyValueColumn from "../../common/KeyValueColumn";
import IntegrationPageContainer from "../integration/IntegrationPageContainer";
import McpPiiProfileSection from "./McpPiiProfileSection";

const setMcpState = withErrorHandling(adminApi.setMcpApplicationState);

const EntriliaMcpPage = () => {
  usePageTitle("Entrilia MCP");

  const { sendNotification, sendNotificationError } = useNotificationContext();
  const { refreshUserData } = useUserContext();
  const { entriliaMcpApp, hasPermissions } = useClientContext();
  const canManage = hasPermissions(["ManageClientIntegrations"]);
  const [enabled, setEnabled] = useState(false);
  const [stateModifiedBy, setStateModifiedBy] = useState<string>();
  const [stateModifiedAt, setStateModifiedAt] = useState<string>();
  const [piiModifiedBy, setPiiModifiedBy] = useState<string>();
  const [piiModifiedAt, setPiiModifiedAt] = useState<string>();
  const [toggling, setToggling] = useState(false);

  const [stateResult, fetchError] = useFetch(adminApi.getMcpApplicationState, {
    onSetData: (data) => {
      setEnabled(data.enabled);
      setStateModifiedBy(data.modifiedBy);
      setStateModifiedAt(data.modifiedAt);
    },
  });

  const handlePiiProfileLoaded = useCallback((modifiedBy: string | undefined, modifiedAt: string | undefined) => {
    setPiiModifiedBy(modifiedBy);
    setPiiModifiedAt(modifiedAt);
  }, []);

  const [displayModifiedBy, displayModifiedAt] =
    stateModifiedAt && piiModifiedAt
      ? new Date(piiModifiedAt).getTime() > new Date(stateModifiedAt).getTime()
        ? [piiModifiedBy, piiModifiedAt]
        : [stateModifiedBy, stateModifiedAt]
      : stateModifiedAt
        ? [stateModifiedBy, stateModifiedAt]
        : [piiModifiedBy, piiModifiedAt];

  const handleToggle = async () => {
    setToggling(true);
    const newValue = !enabled;
    const [response, error] = await setMcpState({ enabled: newValue });
    if (!error) {
      setEnabled(response.enabled);
      setStateModifiedBy(response.modifiedBy);
      setStateModifiedAt(response.modifiedAt);
      sendNotification(`Entrilia MCP ${newValue ? "enabled" : "disabled"} successfully.`);
      refreshUserData();
    } else {
      logError(error, "[EntriliaMcpPage] setMcpApplicationState");
      sendNotificationError(`Failed to ${newValue ? "enable" : "disable"} Entrilia MCP.`);
    }
    setToggling(false);
  };

  if (fetchError) {
    return <DataLoadingFailed title="Could not load MCP application data" />;
  }

  if (!stateResult) {
    return <InlineLoader />;
  }

  return (
    <IntegrationPageContainer
      title="Entrilia MCP"
      Icon={EntriliaMcpIcon}
      overviewText="The MCP Server allows AI assistants such as Claude, ChatGPT, and Cursor, as well as AI agents, to access and interact with your data."
      appStatus={entriliaMcpApp?.status}
      maxWidth="lg"
    >
      <Stack spacing={3}>
        <Stack spacing={1}>
          <Typography variant="subtitle1">Setup</Typography>
          <Grid container spacing={5}>
            <Grid>
              <KeyValueColumn title="Modified by" value={displayModifiedBy || "-"} />
            </Grid>
            <Grid>
              <KeyValueColumn
                title="Modified at"
                value={displayModifiedAt ? convertISODateTime(displayModifiedAt) : "-"}
              />
            </Grid>
          </Grid>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Switch checked={enabled} onChange={handleToggle} disabled={!canManage || toggling} color="primary" />
          <Typography>Enable application</Typography>
        </Stack>
        {enabled && <McpPiiProfileSection canManage={canManage} onProfileLoaded={handlePiiProfileLoaded} />}
      </Stack>
    </IntegrationPageContainer>
  );
};

export default EntriliaMcpPage;
